import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

// CSS
import "./tagPicker.css";

// Components
import SearchBar from "../Other Components/searchBar";
import ItemCard from "../Item Components/itemCard";
import PageLoader from "../Other Components/pageLoader";
import Title from "../Other Components/Title";
import Button from "../Other Components/button";
import TagEditPopup from "../../Pages/Tag Pages/tagEditPopup";
import TagCreationPopup from "../../Pages/Tag Pages/tagCreationPopup";

// Functions and hooks
import { GetTags } from "../../BackendAccess/TagHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";

export default function TagPicker({
  title = "Tags",
  onChange = (selectedTags) => {},
  initSelectedTags,
  singleSelect = false,
  disabled,
  className,
}) {
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);

  const { showPopup, removePopup } = usePopup();

  const { t } = useTranslation();

  const openCreationPopup = () => {
    const key = Date.now();
    showPopup(
      <TagCreationPopup
        onCancel={() => {
          removePopup(key);
        }}
        onCreate={() => {
          removePopup(key);
        }}
      />,
      key
    );
  };
  const openEditPopup = (id) => {
    const key = Date.now();
    showPopup(
      <TagEditPopup
        onCancel={() => {
          removePopup(key);
        }}
        onEdit={() => {
          removePopup(key);
        }}
        currentID={id}
        onDelete={() => {
          removePopup(key);
        }}
      />,
      key
    );
  };

  const finalClassName = twMerge("tag-picker w-100 h-100", className);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const containerRef = useRef(null);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const cardSecondActionHandler = (id) => {
    openEditPopup(id);
  };

  useEffect(() => {
    if (tags.length === 0) GetAllTags();
    console.log("initSelectedTags", initSelectedTags);
    if (
      selectedTags.length === 0 &&
      initSelectedTags &&
      initSelectedTags.length > 0
    )
      setSelectedTags(initSelectedTags);
  }, [initSelectedTags]);

  useEffect(() => {
    onChange(selectedTags);
  }, [selectedTags]);

  const GetAllTags = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetTags();

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setTags(data);
      }
    } catch (error) {
      setError({ err: t("Server error"), errCode: 500 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (term) => {
    if (!disabled) setSearchTerm(term);
  };

  const handleSearchFocus = () => {
    if (!disabled) setSearching(true);
  };

  const handleSearchBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget) && !disabled) {
      setSearching(false);
    }
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !disabled
    ) {
      setSearching(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const filteredTags = tags.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      item.Name.toLowerCase().includes(lowerCaseTerm) ||
      item.TagID.toString().toLowerCase().includes(lowerCaseTerm)
    );
  });

  const sortedTags = [...filteredTags].sort((a, b) =>
    a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1
  );

  const onTagSelectChanged = (id, state) => {
    if (!disabled) {
      var newSelectedIds = [...selectedTags];
      if (state) {
        if (!singleSelect || newSelectedIds.length === 0) {
          newSelectedIds.push(id);
        } else if (newSelectedIds.length !== 0 && singleSelect) {
          newSelectedIds = [];
          newSelectedIds.push(id);
        }
      } else {
        newSelectedIds = newSelectedIds.filter((item) => item !== id);
      }
      setSelectedTags(newSelectedIds);
    }
  };

  const createCardProps = (item) => {
    return [
      { Name: "ID", Value: item.TagID, columnWidth: 1 },
      {
        Name: "Name",
        Value: item.Name,
        columnWidth: 3,
        Color: item.Color,
      },
    ];
  };
  const handleCreateClick = async () => {
    openCreationPopup();
  };

  return (
    <>
      <div className={finalClassName} ref={containerRef} tabIndex={-1}>
        {disabled ? <div className="disabledDimmingCover"></div> : null}
        <Title className="h-auto w-100 font-l mb-1">{title}</Title>
        <div className="tag-picker-content">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            shadow={false}
            placeholder={t("Search tags...")}
          />

          <div className="tag-picker-lists-container">
            <PageLoader loading={loading} error={pageError}>
              <ul
                className={
                  searching ? "tag-picker-list" : "tag-picker-list collapsed"
                }
              >
                {sortedTags.map((item) => (
                  <li
                    key={item.TagID}
                    className="tag-picker-list-item"
                    onClick={handleSearchFocus}
                  >
                    <ItemCard
                      key={item.TagID}
                      ItemID={item.TagID}
                      propertiesToShow={createCardProps(item)}
                      bBtnVisible={false}
                      bBodyClickable={true}
                      bBtnSectionVisible={false}
                      bSelectable={true}
                      onSelected={() => {}}
                      selectedItemIds={selectedTags}
                      setIDSelected={onTagSelectChanged}
                      onSecondAction={cardSecondActionHandler}
                    />
                  </li>
                ))}
              </ul>

              <ul className="picked-tags-list">
                {!loading && selectedTags.length > 0 ? (
                  selectedTags.map((TagID) => {
                    const tag = tags.find((cl) => cl.TagID === TagID);
                    return tag ? (
                      <li key={TagID} className="picked-tags-list-item">
                        <ItemCard
                          key={tag.TagID}
                          ItemID={tag.TagID}
                          propertiesToShow={createCardProps(tag)}
                          bBtnVisible={false}
                          bBodyClickable={true}
                          bBtnSectionVisible={false}
                          bSelectable={true}
                          onSelected={() => {}}
                          selectedItemIds={selectedTags}
                          setIDSelected={onTagSelectChanged}
                        />
                      </li>
                    ) : null;
                  })
                ) : (
                  <p className="cardPlaceholder Tag">{t("No selected tags")}</p> // Placeholder if no tags are selected
                )}
              </ul>
              <Button
                btnSize={"btn-normal"}
                btnStyle={"btn-positive"}
                className=" w-90 mb-2"
                btnMargin={"btn-default"}
                onClick={handleCreateClick}
                bNoShadow={true}
                icon={"create"}
              >
                {t("Create Tag")}
              </Button>
            </PageLoader>
          </div>
        </div>
      </div>
    </>
  );
}
